import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  FaCloudUploadAlt,
  FaFileImage,
  FaCheckCircle,
  FaTimesCircle,
  FaDownload,
  FaFilePdf,
} from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  confirmPayment,
  ConfirmPaymentState,
  resetConfirmPayment,
  selectConfirmPayment,
} from "../slices/confirm-payment.slice";
import {
  rejectPayment,
  RejectPaymentState,
  resetRejectPayment,
  selectRejectPayment,
} from "../slices/reject-payment.slice";
import { OrderPaymentDetails } from "features/stock-ordering/core/domain/payment.model";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import { dateSetup } from "./stock-ordering-utils";

interface PaymentProcessConfirmProps {
  orderId: string | undefined;
  paymentDetails: OrderPaymentDetails | null | undefined;
  onClose: () => void;
  readOnly?: boolean;
}

// Helper to check if a filename is an image
const isImageFile = (filename: string | null | undefined): boolean => {
  if (!filename) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  const lowerFilename = filename.toLowerCase();
  return imageExtensions.some((ext) => lowerFilename.endsWith(ext));
};

// Helper to get the full file URL
const getFileUrl = (filename: string | null | undefined): string => {
  if (!filename) return "";
  return `${REACT_APP_DOMAIN_URL}api/assets/uploads/multim/payment/${filename}`;
};

export function PaymentProcessConfirm(props: PaymentProcessConfirmProps) {
  const { orderId, paymentDetails, onClose, readOnly = false } = props;

  const dispatch = useAppDispatch();
  const confirmPaymentState = useAppSelector(selectConfirmPayment);
  const rejectPaymentState = useAppSelector(selectRejectPayment);

  const [officialReceipt, setOfficialReceipt] = useState<File | null>(null);
  const [remarks, setRemarks] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);

  // Handle successful confirmation
  useEffect(() => {
    if (confirmPaymentState.status === ConfirmPaymentState.success) {
      dispatch(resetConfirmPayment());
      onClose();
    }
  }, [confirmPaymentState.status, dispatch, onClose]);

  // Handle successful rejection
  useEffect(() => {
    if (rejectPaymentState.status === RejectPaymentState.success) {
      dispatch(resetRejectPayment());
      setShowRejectDialog(false);
      onClose();
    }
  }, [rejectPaymentState.status, dispatch, onClose]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, GIF, and PDF files are allowed");
        return;
      }

      setOfficialReceipt(file);

      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleConfirm = () => {
    if (!orderId) return;

    dispatch(
      confirmPayment({
        orderId: Number(orderId),
        officialReceipt: officialReceipt,
        remarks: remarks || undefined,
      }),
    );
  };

  const handleReject = () => {
    if (!orderId || !rejectReason.trim()) return;

    dispatch(
      rejectPayment({
        orderId: Number(orderId),
        reason: rejectReason,
      }),
    );
  };

  const handleDownloadFile = (filename: string | null | undefined) => {
    if (!filename) return;
    const url = getFileUrl(filename);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isConfirming =
    confirmPaymentState.status === ConfirmPaymentState.inProgress;
  const isRejecting =
    rejectPaymentState.status === RejectPaymentState.inProgress;

  const paymentProofIsImage = isImageFile(paymentDetails?.payment_proof_image);
  const paymentProofUrl = getFileUrl(paymentDetails?.payment_proof_image);

  return (
    <div className="space-y-6">
      {/* Section Title */}
      <div className="flex items-center space-x-2 text-lg font-semibold text-gray-800">
        <FaFileImage className="text-blue-600" />
        <span>Payment Verification</span>
      </div>

      {/* Read-only notice for store users */}
      {readOnly && (
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <p className="text-blue-700 text-sm">
            Payment proof has been submitted. Waiting for the supplier to verify
            and confirm.
          </p>
        </div>
      )}

      {/* Submitted Payment Proof */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-md font-semibold text-blue-800 mb-4">
          Submitted Payment Proof
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Payment Proof Image / File */}
          <div className="flex flex-col items-center">
            {paymentDetails?.payment_proof_image ? (
              paymentProofIsImage ? (
                <div
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowImageModal(true)}
                >
                  <img
                    src={paymentProofUrl}
                    alt="Payment Proof"
                    className="max-w-[250px] max-h-[250px] object-contain rounded-lg shadow-md border border-gray-200"
                  />
                  <p className="text-xs text-blue-600 mt-2 text-center">
                    Click to view full size
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-gray-100 rounded-lg p-6">
                    <FaFilePdf className="text-5xl text-red-500 mx-auto" />
                  </div>
                  <p className="text-sm text-gray-600 font-medium">
                    {paymentDetails.payment_proof_image}
                  </p>
                </div>
              )
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <FaFileImage className="text-4xl text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  No payment proof uploaded
                </p>
              </div>
            )}

            {/* Download button - always shown when file exists */}
            {paymentDetails?.payment_proof_image && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FaDownload />}
                onClick={() =>
                  handleDownloadFile(paymentDetails.payment_proof_image)
                }
                sx={{
                  mt: 2,
                  borderColor: "#2563eb",
                  color: "#2563eb",
                  "&:hover": {
                    borderColor: "#1d4ed8",
                    backgroundColor: "rgba(37, 99, 235, 0.04)",
                  },
                }}
              >
                Download File
              </Button>
            )}
          </div>

          {/* Payment Details */}
          <div className="space-y-3">
            {paymentDetails?.payment_proof_uploaded_at && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">
                  Uploaded On
                </p>
                <p className="text-gray-900 font-semibold">
                  {dateSetup(paymentDetails.payment_proof_uploaded_at, true)}
                </p>
              </div>
            )}

            {paymentDetails?.uploaded_by_name && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">
                  Uploaded By
                </p>
                <p className="text-gray-900 font-semibold">
                  {paymentDetails.uploaded_by_name}
                </p>
              </div>
            )}

            {paymentDetails?.payment_remarks && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">
                  Remarks from Store
                </p>
                <p className="text-gray-700 text-sm bg-white p-2 rounded border">
                  {paymentDetails.payment_remarks}
                </p>
              </div>
            )}

            {paymentDetails?.rejected_at && (
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-xs text-red-500 uppercase font-medium">
                  Previously Rejected
                </p>
                <p className="text-red-700 text-sm">
                  {paymentDetails.rejection_reason || "No reason provided"}
                </p>
                <p className="text-xs text-red-400 mt-1">
                  {dateSetup(paymentDetails.rejected_at, true)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions section - only shown for sellers (not readOnly) */}
      {!readOnly && (
        <>
          {/* Upload Official Receipt */}
          <div className="space-y-3">
            <h3 className="text-md font-semibold text-gray-800 flex items-center space-x-2">
              <FaCloudUploadAlt className="text-green-600" />
              <span>Upload Official Receipt (OR)</span>
              <span className="text-gray-400 text-sm font-normal">
                (Optional)
              </span>
            </h3>

            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                officialReceipt
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 hover:border-green-500"
              }`}
            >
              <input
                type="file"
                id="officialReceipt"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="officialReceipt"
                className="cursor-pointer flex flex-col items-center"
              >
                {previewUrl ? (
                  <div className="space-y-3">
                    <img
                      src={previewUrl}
                      alt="Official Receipt preview"
                      className="max-w-[200px] max-h-[200px] object-contain rounded-lg shadow-md"
                    />
                    <p className="text-sm text-green-600 font-medium">
                      {officialReceipt?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Click to change file
                    </p>
                  </div>
                ) : officialReceipt ? (
                  <div className="space-y-3">
                    <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-3xl">📄</span>
                    </div>
                    <p className="text-sm text-green-600 font-medium">
                      {officialReceipt.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Click to change file
                    </p>
                  </div>
                ) : (
                  <>
                    <FaCloudUploadAlt className="text-4xl text-gray-400 mb-3" />
                    <p className="text-gray-600 font-medium">
                      Drop Official Receipt here or click to upload
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Accepted: JPG, PNG, GIF, PDF (Max 5MB)
                    </p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Confirmation Remarks */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Confirmation Remarks (Optional)
            </label>
            <TextField
              multiline
              rows={3}
              fullWidth
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any notes about this payment confirmation..."
              variant="outlined"
              size="small"
            />
          </div>

          {/* Error Messages */}
          {confirmPaymentState.status === ConfirmPaymentState.fail && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {confirmPaymentState.message ||
                "Failed to confirm payment. Please try again."}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowRejectDialog(true)}
              disabled={isConfirming || isRejecting}
              startIcon={<FaTimesCircle />}
            >
              Reject Payment
            </Button>

            <div className="flex space-x-3">
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isConfirming || isRejecting}
                sx={{
                  color: "gray",
                  borderColor: "gray",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={isConfirming || isRejecting}
                sx={{
                  backgroundColor: "#16a34a",
                  "&:hover": {
                    backgroundColor: "#15803d",
                  },
                }}
                startIcon={
                  isConfirming ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FaCheckCircle />
                  )
                }
              >
                {isConfirming ? "Confirming..." : "Confirm Payment"}
              </Button>
            </div>
          </div>

          {/* Reject Dialog */}
          <Dialog
            open={showRejectDialog}
            onClose={() => setShowRejectDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ backgroundColor: "#dc2626", color: "white" }}>
              Reject Payment
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <p className="text-gray-600 mb-4">
                Please provide a reason for rejecting this payment. The store
                will be notified and will need to submit a new payment proof.
              </p>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                variant="outlined"
                required
              />
              {rejectPaymentState.status === RejectPaymentState.fail && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mt-3">
                  {rejectPaymentState.message ||
                    "Failed to reject payment. Please try again."}
                </div>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setShowRejectDialog(false)}
                disabled={isRejecting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleReject}
                disabled={!rejectReason.trim() || isRejecting}
                startIcon={
                  isRejecting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {isRejecting ? "Rejecting..." : "Confirm Rejection"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Image Modal - for image files only */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0 }}>
          {paymentDetails?.payment_proof_image && paymentProofIsImage && (
            <img
              src={paymentProofUrl}
              alt="Payment Proof Full Size"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<FaDownload />}
            onClick={() =>
              handleDownloadFile(paymentDetails?.payment_proof_image)
            }
          >
            Download
          </Button>
          <Button onClick={() => setShowImageModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
